// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Region } from '../types';
import { num, rate, delta, esc, REMOTENESS_SHORT, CHANNEL_COLOUR } from '../format';
import { spark } from '../charts';
import { openRegion } from '../main';

const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
type Key = 'name' | 'state' | 'branchNow' | 'branch2017' | 'branchChange' | 'perCapita' | 'bankpostNow';

export function renderExplorer(root: HTMLElement, data: Dataset, filter?: string): void {
  let query = filter ?? '';
  let key: Key = 'branchNow';
  let asc = false;
  const years = data.national.years;

  root.innerHTML = `
    <div class="view-head">
      <h2>Explore every region</h2>
      <p>All ${data.regions.length} SA3 regions. Search a suburb or town, sort by any column, click a row for the full record and year-by-year history.</p>
    </div>
    <div class="controls">
      <input type="search" id="q" placeholder="Search a region…" value="${esc(query)}" aria-label="Search region" />
      <span class="control-label" id="count"></span>
    </div>
    <div class="panel">
      <div class="table-scroll">
        <table>
          <thead><tr>
            <th class="sortable" data-key="name">Region</th>
            <th class="sortable" data-key="state">State</th>
            <th>Remoteness</th>
            <th class="right sortable" data-key="branchNow">Branches now</th>
            <th class="right sortable" data-key="branch2017">${years[0]}</th>
            <th class="right sortable" data-key="branchChange">Change</th>
            <th class="right sortable" data-key="perCapita">/ 10k</th>
            <th class="right sortable" data-key="bankpostNow">Bank@Post</th>
            <th>Trend</th>
          </tr></thead>
          <tbody id="rows"></tbody>
        </table>
      </div>
    </div>
  `;

  const rowsEl = root.querySelector('#rows') as HTMLElement;
  const countEl = root.querySelector('#count') as HTMLElement;
  const input = root.querySelector('#q') as HTMLInputElement;

  function val(r: Region, k: Key): number | string | null {
    if (k === 'name') return r.name;
    if (k === 'state') return r.state ?? '';
    return (r as any)[k];
  }

  function draw(): void {
    let rows = data.regions.filter((r) => !query || norm(r.name).includes(norm(query)) || (r.state ?? '').toLowerCase() === query.toLowerCase());
    rows.sort((a, b) => {
      const va = val(a, key), vb = val(b, key);
      if (typeof va === 'string' || typeof vb === 'string') return asc ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
      const na = va == null ? -Infinity : va, nb = vb == null ? -Infinity : vb;
      return asc ? na - nb : nb - na;
    });
    rowsEl.innerHTML = rows.slice(0, 300).map((r) => `
      <tr class="clickable" data-code="${esc(r.code)}">
        <td class="region-name">${esc(r.name)}</td>
        <td><span class="state-pill">${esc(r.state ?? '—')}</span></td>
        <td>${r.remoteness ? esc(REMOTENESS_SHORT[r.remoteness] ?? r.remoteness) : '—'}</td>
        <td class="right num">${num(r.branchNow)}</td>
        <td class="right num">${num(r.branch2017)}</td>
        <td class="right num ${r.branchChangeAbs < 0 ? 'down' : r.branchChangeAbs > 0 ? 'up' : ''}">${r.branchChange == null ? '—' : delta(r.branchChange)}</td>
        <td class="right num">${rate(r.perCapita, 2)}</td>
        <td class="right num">${num(r.bankpostNow)}</td>
        <td>${spark(r.series.branch, { w: 78, h: 22, colour: CHANNEL_COLOUR.branch })}</td>
      </tr>`).join('');
    countEl.textContent = `${rows.length} regions${rows.length > 300 ? ' (showing first 300)' : ''}`;
    rowsEl.querySelectorAll<HTMLElement>('[data-code]').forEach((tr) => tr.addEventListener('click', () => openRegion(tr.dataset.code!)));
  }

  let t: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(t);
    t = window.setTimeout(() => { query = input.value; draw(); }, 250);
  });
  root.querySelectorAll<HTMLElement>('.sortable').forEach((th) =>
    th.addEventListener('click', () => {
      const k = th.dataset.key as Key;
      if (key === k) asc = !asc; else { key = k; asc = k === 'name' || k === 'state'; }
      draw();
    }),
  );

  draw();
}
